import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { Button as Button95, Hourglass, TextInput } from 'react95-native-rabbl';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from "@/utilities/AppUtils";
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { BasicScrollView } from "@/components/BasicScrollView";
import { AppText } from '@/components/AppText';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
  },
  formElement: {
    maxWidth: 300,
  },
  inputWrapper: {
    flexDirection: 'row',
  },
});

export default function DeleteData() {
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const nameInput = useRef<RNTextInput>(null);
  const emailInput = useRef<RNTextInput>(null);
  const detailsInput = useRef<RNTextInput>(null);
  const reduxState = useSelector((state: RootState) => state);

  const { register, control, handleSubmit, setValue, reset, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      email: '',
      details: '',
    }
  });

  const onSubmit = useCallback(async (data: any) => {
    try {
      showSpinner();

      let formInput = {};

      (formInput as any)['delete'] = "Request to delete all data.";
      (formInput as any)['thoughts'] = data.details;
      (formInput as any)['log'] = AppUtilities.getAccumulatedDebug();
      (formInput as any)['state'] = reduxState;

      await AppUtilities.fetchUnauthorized(
        "DeleteData.onSubmit",
        "/rabbl/api/contact",
        "POST",
        {
          name: data.name,
          email: data.email,
          details: formInput,
        }
      );

      AppUtils.showInformationalToast('Request sent successfully', "", "done");
      reset();

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(data), true);
      AppUtilities.handleException(e, "DeleteData.onSubmit");
    } finally {
      hideSpinner();
    }
  }, [reduxState]);

  async function onValidationErrors(errors: any) {
    AppUtils.showErrorToast('There are some errors. Review and try again.');
  };

  useEffect(() => {
    if (AppUtilities.shouldAutoFocus()) {
      if (nameInput && nameInput.current) {
        nameInput.current?.focus();
      }
    }
  }, [nameInput]);

  return (
    <BasicScrollView center={false}>
      <Meta title={"Delete data"} />
      <AppText>Enter your name, email and any background information to request deletion of all your data:</AppText>

      <Controller
        control={control}
        name="name"
        rules={{
          required: true,
        }}
        render={({ field: { onChange, onBlur, value } }) => (
          <TextInput
            ref={nameInput}
            value={value}
            onChangeText={onChange}
            placeholder="Your name"
            style={styles.formElement}
            returnKeyType="next"
            onBlur={onBlur}
            autoComplete="name"
            textContentType="name"
            onSubmitEditing={() => emailInput.current?.focus()}
          />
        )}
      />
      {errors.name && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^^ Name is required.</AppText>}

      <Controller
        control={control}
        name="email"
        rules={{
          required: true,
        }}
        render={({ field: { onChange, onBlur, value } }) => (
          <TextInput
            ref={emailInput}
            value={value}
            onChangeText={onChange}
            placeholder="Your email"
            style={styles.formElement}
            returnKeyType="next"
            onBlur={onBlur}
            autoComplete="email"
            keyboardType="email-address"
            textContentType="emailAddress"
            autoCapitalize='none'
            onSubmitEditing={() => detailsInput.current?.focus()}
          />
        )}
      />
      {errors.email && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^^ Email is required.</AppText>}

      <Controller
        control={control}
        name="details"
        rules={{
          required: false,
        }}
        render={({ field: { onChange, onBlur, value } }) => (
          <TextInput
            ref={detailsInput}
            value={value}
            onChangeText={onChange}
            placeholder="Background details"
            multiline={true}
            numberOfLines={5}
            returnKeyType="send"
            onBlur={onBlur}
            textAlignVertical="top"
            inputStyle={AppConstants.STYLES.textInputMultiline}
          />
        )}
      />

      <View style={styles.inputWrapper}>
        <Button95 onPress={handleSubmit(onSubmit, onValidationErrors)}>Request deletion</Button95>
      </View>
      
      <Footer />
    </BasicScrollView>
  );
}
